import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AddBtn } from "@/App/Clients/Add";
import { UpdateBtn } from "@/App/Clients/Update";
import { useEffect, useState } from "react";
import { Mask } from "@/utils/mask";
import { getSelectAllClientsAndLeads } from "@/Api/clients";
import { Phone, Mail, MapPin, User } from "lucide-react";

const getWhatsAppLink = (phone: string) => {
  const cleanPhone = phone?.replace(/\D/g, '') || '';
  return `https://wa.me/${cleanPhone}`;
};

const isValidValue = (value: string | null | undefined): boolean => {
  return !!value && value !== "null" && value !== "undefined" && value.trim() !== "";
};

const looksLikeFullAddress = (value: string | null | undefined): boolean => {
  if (!isValidValue(value)) return false;
  return value!.includes(',') || value!.includes(' - ') || value!.includes('Rua') || value!.includes('R.') || value!.includes('Av.');
};

const buildAddressName = (addr: {
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
}): string => {
  // Se city parece conter um endereço completo (dados legados salvos incorretamente), usar ele
  if (looksLikeFullAddress(addr.city) && !isValidValue(addr.street)) {
    return addr.city!;
  }

  const parts: string[] = [];

  if (isValidValue(addr.street)) {
    if (isValidValue(addr.number)) {
      parts.push(`${addr.street}, ${addr.number}`);
    } else {
      parts.push(addr.street!);
    }
  }

  if (isValidValue(addr.neighborhood)) {
    parts.push(addr.neighborhood!);
  }

  if (isValidValue(addr.city) && isValidValue(addr.state)) {
    parts.push(`${addr.city}/${addr.state}`);
  } else if (isValidValue(addr.city)) {
    parts.push(addr.city!);
  } else if (isValidValue(addr.state)) {
    parts.push(addr.state!);
  }

  if (parts.length === 0 && (addr.latitude || addr.longitude)) {
    return "";
  }

  return parts.length > 0 ? parts.join(' - ') : "";
};

interface ClientAddress {
  id: number;
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  zip_code: string;
  latitude?: number | string | null;
  longitude?: number | string | null;
  pivot: {
    client_id: number;
    address_id: number;
    is_main: number;
    created_at: string;
    updated_at: string;
  };
}

interface Client {
  id: number;
  uuid: string;
  name: string;
  document?: string;
  phone: string;
  image?: string;
  email: string;
  discount?: number;
  discount_type_id?: number;
  client_origin_id?: number;
  client_category_id?: number;
  is_active?: number;
  tenant_id?: number;
  seller_id?: number;
  created_at?: string;
  updated_at?: string;
  deleted_at?: null | string;
  discount_type?: {
    id: number;
    name: string;
  };
  client_category?: {
    id: number;
    name: string;
    establishment_id: number;
  };
  client_origin?: {
    id: number;
    name: string;
    establishment_id: number;
  };
  seller?: {
    id: number;
    user?: {
      name: string;
    };
  };
  addresses?: ClientAddress[];
  isLead?: boolean;
}

interface PaginatedResponse {
  current_page: number;
  data: Client[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

interface SelectAllResponse {
  status: boolean;
  response: {
    clients: Client[];
    leads: Client[];
  };
  message: string;
}

interface ClientSelectionProps {
  clients: PaginatedResponse;
  sellerId: string;
  selectedClient: string;
  onClientSelect: (clientId: string, isLead?: boolean, name?: string, uuid?: string, discount?: number, discountType?: { id: number; name: string }) => void;
  onNewClient: () => void;
  address: {
    addressName: string;
    street: string;
    number: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    latitude?: string;
    longitude?: string;
  };
  onAddressChange: (address: {
    addressName: string;
    street: string;
    number: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    latitude?: string;
    longitude?: string;
  }) => void;
}

export const ClientSelection = ({
  selectedClient,
  onClientSelect,
  onAddressChange,
  sellerId
}: ClientSelectionProps) => {
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [allContacts, setAllContacts] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState<"client" | "lead" | null>(null);

  useEffect(() => {
    fetchClientsAndLeads();
  }, []);

  const fetchClientsAndLeads = async (name?: string, phone?: string) => {
    setIsLoading(true);
    try {
      const params: { name?: string; phone?: string; seller_id?: string } = {};
      if (name) params.name = name;
      if (phone) params.phone = phone;
      if (sellerId) params.seller_id = sellerId;

      const response = await getSelectAllClientsAndLeads(params);
      if (response && response.data) {
        const data = response.data as SelectAllResponse;
        if (data.status && data.response) {
          // Merge clients and leads into a single array
          const clients = data.response.clients.map(client => ({
            ...client,
            isLead: false
          }));
          
          const leads = data.response.leads.map(lead => ({
            ...lead,
            isLead: true,
            addresses: [] // ensure leads have an empty addresses array
          }));
          
          setAllContacts([...clients, ...leads]);
        }
      }
    } catch (error) {
      console.error("Error fetching clients and leads:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchSubmit = () => {
    const isPhone = /^\d+$/.test(searchTerm.replace(/\D/g, '')) && searchTerm.replace(/\D/g, '').length >= 8;
    if (isPhone) {
      fetchClientsAndLeads(undefined, searchTerm);
    } else {
      fetchClientsAndLeads(searchTerm, undefined);
    }
  };
  useEffect(() => {
    const client = allContacts?.find((c) => String(c?.id) == selectedClient);
    if (client && client.addresses && client.addresses.length > 0) {
      const firstAddress = client.addresses[0];
      setSelectedAddressId(firstAddress.id);

      const addressName = buildAddressName(firstAddress);

      onAddressChange({
        addressName,
        street: isValidValue(firstAddress.street) ? firstAddress.street : "",
        number: isValidValue(firstAddress.number) ? firstAddress.number : "",
        complement: "",
        neighborhood: isValidValue(firstAddress.neighborhood) ? firstAddress.neighborhood : "",
        city: isValidValue(firstAddress.city) ? firstAddress.city : "",
        state: isValidValue(firstAddress.state) ? firstAddress.state : "",
        zip_code: isValidValue(firstAddress.zip_code) ? firstAddress.zip_code : "",
        latitude: firstAddress.latitude?.toString() || "",
        longitude: firstAddress.longitude?.toString() || "",
      });
    } else {
      setSelectedAddressId(null);
    }
  }, [selectedClient]);

  const handleClientSelect = (id: string, isLead: boolean) => {
    setSelectedItemType(isLead ? "lead" : "client");
    const selectedContact = sortedContacts.find(contact => String(contact.id) === id);
    if (selectedContact) {
      onClientSelect(
        id,
        isLead,
        selectedContact.name || "",
        selectedContact.uuid || "",
        selectedContact.discount,
        selectedContact.discount_type
      );
    }
  };

  const sortedContacts = [...allContacts].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="flex flex-col gap-6 p-6">
      <h2 className="text-2xl font-semibold text-center">
        Selecione o Cliente
      </h2>
      <div className="flex flex-col gap-4">
        <div className="flex justify-center">
          <div className="w-full max-w-2xl">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome ou Telefone
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
              placeholder="Digite o nome ou telefone"
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
            />
          </div>
        </div>
        <div className="flex justify-center gap-4">
          <button
            onClick={handleSearchSubmit}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Buscar
          </button>
          <AddBtn updateFront={() => handleSearchSubmit()} />
        </div>
      </div>

      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
        <ScrollArea className="h-[400px]">
          {isLoading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-2">
            {sortedContacts.map((contact) => (
              <Card
                key={`${contact.isLead ? 'lead' : 'client'}-${contact.id}`}
                className={`cursor-pointer transition-all border-2 rounded-lg ${
                  selectedClient === String(contact.id) &&
                  (contact.isLead ? selectedItemType === "lead" : selectedItemType === "client")
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200"
                } hover:shadow-lg hover:border-gray-300`}
                onClick={() => handleClientSelect(String(contact.id), contact.isLead || false)}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col gap-3">
                    {/* Header com nome e botão de edição */}
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex flex-col">
                          <h3 className="font-bold text-lg text-gray-800 leading-tight">
                            {contact.name}
                          </h3>
                          {contact.seller?.user?.name && (
                            <span className="text-sm text-gray-500">
                              <span className="font-semibold">Vendedor:</span> {contact.seller.user.name}
                            </span>
                          )}
                        </div>
                      </div>
                      {!contact.isLead && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <UpdateBtn
                            updateFront={() => handleSearchSubmit()}
                            uuid={contact.uuid}
                          />
                        </div>
                      )}
                    </div>

                    {/* Separador abaixo do nome */}
                    <hr className="border-gray-200" />

                    {/* Email */}
                    {contact.email && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="break-all">{contact.email}</span>
                      </div>
                    )}

                    {/* Telefone com WhatsApp */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="font-medium">{Mask.maskPhone(contact?.phone || '')}</span>
                      {contact.phone && (
                        <a
                          href={getWhatsAppLink(contact.phone)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="ml-auto p-1.5 rounded-full bg-green-500 hover:bg-green-600 transition-colors"
                          title="Abrir WhatsApp"
                        >
                          <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                        </a>
                      )}
                    </div>

                    {/* Separador entre contato e endereço */}
                    {!contact.isLead && contact.addresses && contact.addresses.length > 0 && (
                      <>
                        <hr className="border-gray-200" />
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <h4 className="text-sm font-semibold text-gray-700">Endereço</h4>
                          </div>
                          {selectedClient === String(contact.id) &&
                          selectedItemType === "client" &&
                          contact.addresses && contact.addresses.length > 0 ? (
                            <select
                              value={selectedAddressId ?? undefined}
                              onChange={(e) => {
                                if (!contact.addresses) return;
                                
                                const addressId = Number(e.target.value);
                                setSelectedAddressId(addressId);
                                const newAddress = contact.addresses.find(
                                  (addr) => addr.id === addressId
                                );
                                if (newAddress) {
                                  const addressName = buildAddressName(newAddress);

                                  onAddressChange({
                                    addressName,
                                    street: isValidValue(newAddress.street) ? newAddress.street : "",
                                    number: isValidValue(newAddress.number) ? newAddress.number : "",
                                    complement: "",
                                    neighborhood: isValidValue(newAddress.neighborhood) ? newAddress.neighborhood : "",
                                    city: isValidValue(newAddress.city) ? newAddress.city : "",
                                    state: isValidValue(newAddress.state) ? newAddress.state : "",
                                    zip_code: isValidValue(newAddress.zip_code) ? newAddress.zip_code : "",
                                    latitude: newAddress.latitude?.toString() || "",
                                    longitude: newAddress.longitude?.toString() || "",
                                  });
                                }
                              }}
                              className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white"
                            >
                              {contact.addresses.map((addr) => {
                                const addressName = buildAddressName(addr);
                                const hasLocation = addr.latitude || addr.longitude;

                                return (
                                  <option key={addr.id} value={addr.id}>
                                    {addressName || (hasLocation ? "Localização salva (ver mapa)" : "Endereço não informado")}
                                  </option>
                                );
                              })}
                            </select>
                          ) : (
                            <div className="text-sm text-gray-600">
                              {(() => {
                                const addr = contact.addresses[0];
                                const addressName = buildAddressName(addr);
                                const hasLocation = addr.latitude || addr.longitude;

                                if (!addressName && !hasLocation) {
                                  return (
                                    <p className="text-gray-400 italic">
                                      Endereço não informado
                                    </p>
                                  );
                                }

                                if (!addressName && hasLocation) {
                                  return (
                                    <p className="text-blue-600 italic">
                                      Localização salva (ver mapa na entrega)
                                    </p>
                                  );
                                }

                                return (
                                  <p className="font-medium">{addressName}</p>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Caso não tenha endereço */}
                    {!contact.isLead && (!contact.addresses || contact.addresses.length === 0) && (
                      <>
                        <hr className="border-gray-200" />
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <h4 className="text-sm font-semibold text-gray-700">Endereço</h4>
                          </div>
                          <p className="text-sm text-gray-400 italic pl-6">
                            Nenhum endereço cadastrado
                          </p>
                        </div>
                      </>
                    )}

                    {/* Indicador de tipo (Lead) */}
                    {contact.isLead && (
                      <>
                        <hr className="border-gray-200" />
                        <div className="flex justify-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Lead
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>
          )}
        </ScrollArea>

        {!isLoading && allContacts.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            Nenhum cliente encontrado
          </div>
        )}
      </div>

      {/* Se um cliente e endereço estiverem selecionados, exibe os campos para manutenção/alteração */}
      {/* {selectedClient && selectedAddressId && selectedItemType === "client" && (
        <div className="mt-6 p-4 border border-gray-300 rounded">
          <h3 className="text-xl font-semibold mb-4">Endereço Selecionado</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Rua
              </label>
              <input
                type="text"
                value={address.street}
                onChange={(e) =>
                  onAddressChange({
                    ...address,
                    street: e.target.value,
                    addressName: `${e.target.value}, ${address.number}`,
                  })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Número
              </label>
              <input
                type="text"
                value={address.number}
                onChange={(e) =>
                  onAddressChange({
                    ...address,
                    number: e.target.value,
                    addressName: `${address.street}, ${e.target.value}`,
                  })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Complemento
              </label>
              <input
                type="text"
                value={address.complement || ""}
                onChange={(e) =>
                  onAddressChange({ ...address, complement: e.target.value })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Bairro
              </label>
              <input
                type="text"
                value={address.neighborhood || ""}
                onChange={(e) =>
                  onAddressChange({ ...address, neighborhood: e.target.value })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Cidade
              </label>
              <input
                type="text"
                value={address.city || ""}
                onChange={(e) =>
                  onAddressChange({ ...address, city: e.target.value })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Estado
              </label>
              <input
                type="text"
                value={address.state || ""}
                onChange={(e) =>
                  onAddressChange({ ...address, state: e.target.value })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
              />
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};
